/**
 * Bulk / project-wide export orchestration.
 *
 * The single-form export (data-export-api) covers one form to one CSV/XLSX. This
 * layer adds the cross-form shapes the export page offers:
 *
 *   - data for many forms (a whole group, or the whole project)
 *   - form-schema JSON (form / group / project) — reuses form-builder's `fetchExport`
 *   - the "whole project" bundle = schema.json + per-form data + manifest.json
 *
 * Output shape is decided here: a single file when only one logical artifact is
 * produced (one form's data, one workbook, or one schema JSON), otherwise a ZIP.
 */
import { supabase } from '@/lib/supabase/client'
import { parseDefinition } from '@/features/form-builder/api/form-builder-api'
import { fetchExport } from '@/features/form-builder/api/form-io'
import type { ExportSource } from '@/features/form-builder/api/form-io-core'
import type { FieldDefinition, LocalizedString } from '@/features/form-builder/api/form-builder-types'
import {
  fetchSubmissions,
  buildMatrix,
  type ExportFormat,
  type ExportLayout,
  type HeaderStrategy,
  type ExportResult,
} from './data-export-api'
import {
  buildCsv,
  buildWorkbookBytes,
  downloadZip,
  triggerDownload,
  type NamedTable,
  type ZipEntry,
} from '../lib/bundle'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export type DataScope = 'form' | 'group' | 'project'
/** When exporting many forms to XLSX: one workbook (sheet per form) or one file each. */
export type MultiXlsxMode = 'workbook' | 'separate'

function fa(v: unknown): string {
  if (v && typeof v === 'object' && 'fa' in (v as Record<string, unknown>)) {
    return (v as LocalizedString).fa ?? ''
  }
  return typeof v === 'string' ? v : ''
}

// ─── Resolve forms + their field definitions ─────────────────────────────────

interface FormDefForExport {
  id: string
  formKey: string
  title: string
  fields: FieldDefinition[]
}

interface FormRow {
  id: string
  form_key: string
  title: LocalizedString | null
  current_version_id: string | null
}

/**
 * For data export we want the columns the data was actually filled against —
 * published definition preferred, draft as fallback (handles never-published
 * forms in a bulk run). Forms with neither still resolve to an empty field set
 * (only display_id + instance_index columns).
 */
export async function resolveFormsForExport(
  projectId: string,
  scope: DataScope,
  opts: { groupId?: string; formId?: string },
): Promise<FormDefForExport[]> {
  const cols = 'id, form_key, title, current_version_id'
  let q = supabase.from('forms').select(cols).eq('is_deleted', false)
  if (scope === 'form') {
    if (!opts.formId) throw new Error('formId is required for form scope')
    q = q.eq('id', opts.formId)
  } else if (scope === 'group') {
    if (!opts.groupId) throw new Error('groupId is required for group scope')
    q = q.eq('group_id', opts.groupId).order('display_order')
  } else {
    q = q.eq('project_id', projectId).order('display_order')
  }
  const { data, error } = await q
  if (error) throw error
  const rows = (data ?? []) as FormRow[]

  // Batch-fetch published snapshots + drafts in one round-trip each.
  const versionIds = rows.map((r) => r.current_version_id).filter((v): v is string => !!v)
  const formIds = rows.map((r) => r.id)
  const versionDef = new Map<string, unknown>()
  const draftDef = new Map<string, unknown>()

  if (versionIds.length) {
    const { data: vs, error: vErr } = await supabase
      .from('form_versions')
      .select('id, definition_snapshot')
      .in('id', versionIds)
    if (vErr) throw vErr
    for (const v of vs ?? []) versionDef.set(v.id, v.definition_snapshot)
  }
  if (formIds.length) {
    const { data: ds, error: dErr } = await supabase
      .from('form_drafts')
      .select('form_id, draft_definition')
      .in('form_id', formIds)
    if (dErr) throw dErr
    for (const d of ds ?? []) draftDef.set(d.form_id, d.draft_definition)
  }

  return rows.map((r) => {
    const pub = r.current_version_id ? versionDef.get(r.current_version_id) : null
    const raw = pub ?? draftDef.get(r.id) ?? null
    const fields = raw != null ? parseDefinition(raw).fields : []
    return { id: r.id, formKey: r.form_key, title: fa(r.title), fields }
  })
}

// ─── Build per-form data matrices ─────────────────────────────────────────────

export interface FormDataResult {
  formKey: string
  title: string
  result: ExportResult
}

export interface BuildDataArgs {
  layout: ExportLayout
  headerStrategy: HeaderStrategy
  /** Restrict columns (single-form mode). Omit to export every non-array field. */
  selectedKeys?: string[]
}

/** Fetch + build a matrix for each form; forms with zero submissions are dropped. */
export async function buildFormsData(
  forms: FormDefForExport[],
  args: BuildDataArgs,
): Promise<{ results: FormDataResult[]; skipped: number }> {
  // Fetch every form's submissions in parallel, preserving form order.
  const submissionSets = await Promise.all(forms.map((f) => fetchSubmissions(f.id)))

  const results: FormDataResult[] = []
  let skipped = 0
  forms.forEach((f, i) => {
    const subs = submissionSets[i]
    if (subs.length === 0) {
      skipped++
      return
    }
    const exportable = f.fields.filter((x) => x.field_type !== 'repeatable_array')
    let fieldKeys = exportable.map((x) => x.field_key)
    if (args.selectedKeys) fieldKeys = fieldKeys.filter((k) => args.selectedKeys!.includes(k))
    const result = buildMatrix(subs, {
      fields: f.fields,
      fieldKeys,
      layout: args.layout,
      headerStrategy: args.headerStrategy,
    })
    results.push({ formKey: f.formKey, title: f.title, result })
  })
  return { results, skipped }
}

// ─── Filename helpers ─────────────────────────────────────────────────────────

function stamp(): string {
  return new Date().toISOString().slice(0, 10)
}

function slugify(s: string | undefined): string {
  return (
    (s ?? 'project')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .slice(0, 32) || 'project'
  )
}

// ─── Top-level export ─────────────────────────────────────────────────────────

export interface ExportRequest {
  projectId: string
  scope: DataScope
  groupId?: string
  formId?: string
  includeData: boolean
  includeSchema: boolean
  // data options
  format: ExportFormat
  layout: ExportLayout
  headerStrategy: HeaderStrategy
  xlsxMode: MultiXlsxMode
  selectedKeys?: string[]
  // schema options
  schemaSource: ExportSource
  // naming
  projectName?: string
  scopeLabel?: string
}

export interface ExportSummary {
  filename: string
  formsWithData: number
  formsSkipped: number
  totalRows: number
  schemaForms: number
  zipped: boolean
}

/**
 * Run an export per `req`. Produces a single file when exactly one artifact
 * results (one form's data, one combined workbook, or one schema JSON);
 * otherwise zips everything (data files + schema.json + manifest.json).
 */
export async function runDataExport(req: ExportRequest): Promise<ExportSummary> {
  if (!req.includeData && !req.includeSchema) {
    throw new Error('چیزی برای خروجی انتخاب نشده است.')
  }

  const slug = slugify(req.projectName)
  const isMultiForm = req.scope !== 'form'

  // 1. Schema envelope (optional).
  let schemaJson: string | null = null
  let schemaForms = 0
  if (req.includeSchema) {
    const envelope = await fetchExport({
      projectId: req.projectId,
      scope: req.scope,
      source: req.schemaSource,
      groupId: req.groupId,
      formId: req.formId,
    })
    schemaJson = JSON.stringify(envelope, null, 2)
    schemaForms = envelope.groups.reduce((n, g) => n + g.forms.length, 0)
  }

  // 2. Data matrices (optional).
  let dataResults: FormDataResult[] = []
  let skipped = 0
  if (req.includeData) {
    const forms = await resolveFormsForExport(req.projectId, req.scope, {
      groupId: req.groupId,
      formId: req.formId,
    })
    const built = await buildFormsData(forms, {
      layout: req.layout,
      headerStrategy: req.headerStrategy,
      selectedKeys: req.scope === 'form' ? req.selectedKeys : undefined,
    })
    dataResults = built.results
    skipped = built.skipped
  }

  if (req.includeData && dataResults.length === 0 && !req.includeSchema) {
    throw new Error('هیچ‌کدام از فرم‌ها داده‌ای برای خروجی ندارند.')
  }

  const totalRows = dataResults.reduce((n, r) => n + r.result.rowCount, 0)

  // 3. Decide output shape.
  // A combined XLSX workbook is one artifact; CSV / separate-XLSX is one per form.
  const useWorkbook =
    req.includeData && req.format === 'xlsx' && (isMultiForm ? req.xlsxMode === 'workbook' : true)
  const dataFileCount = req.includeData
    ? useWorkbook
      ? dataResults.length > 0
        ? 1
        : 0
      : dataResults.length
    : 0
  const schemaFileCount = req.includeSchema ? 1 : 0
  const totalArtifacts = dataFileCount + schemaFileCount

  // Single-artifact fast paths (no ZIP).
  if (totalArtifacts <= 1) {
    if (schemaFileCount === 1 && dataFileCount === 0) {
      const filename = `bluedatalake-${slug}-${req.scope}-${stamp()}.json`
      triggerDownload(new Blob([schemaJson!], { type: 'application/json' }), filename)
      return { filename, formsWithData: 0, formsSkipped: skipped, totalRows: 0, schemaForms, zipped: false }
    }
    // Single data artifact.
    if (dataResults.length === 1 && !useWorkbook) {
      const only = dataResults[0]
      const filename = `${only.formKey}-data-${stamp()}.${req.format}`
      if (req.format === 'csv') {
        triggerDownload(
          new Blob([buildCsv(only.result.headers, only.result.matrix)], { type: 'text/csv;charset=utf-8' }),
          filename,
        )
      } else {
        const bytes = await buildWorkbookBytes(
          [{ sheetName: only.formKey, headers: only.result.headers, matrix: only.result.matrix }],
          { title: only.title, generatedAt: new Date().toLocaleString('fa-IR') },
        )
        triggerDownload(new Blob([bytes as BlobPart], { type: XLSX_MIME }), filename)
      }
      return { filename, formsWithData: 1, formsSkipped: skipped, totalRows, schemaForms: 0, zipped: false }
    }
    if (useWorkbook && dataResults.length >= 1) {
      const tables: NamedTable[] = dataResults.map((r) => ({
        sheetName: r.formKey,
        headers: r.result.headers,
        matrix: r.result.matrix,
      }))
      const bytes = await buildWorkbookBytes(tables, {
        title: req.scopeLabel ?? req.projectName ?? 'خروجی',
        generatedAt: new Date().toLocaleString('fa-IR'),
      })
      const filename = `${slug}-data-${stamp()}.xlsx`
      triggerDownload(new Blob([bytes as BlobPart], { type: XLSX_MIME }), filename)
      return { filename, formsWithData: dataResults.length, formsSkipped: skipped, totalRows, schemaForms: 0, zipped: false }
    }
  }

  // 4. ZIP bundle.
  const entries: ZipEntry[] = []

  if (req.includeData) {
    if (useWorkbook && dataResults.length > 0) {
      const tables: NamedTable[] = dataResults.map((r) => ({
        sheetName: r.formKey,
        headers: r.result.headers,
        matrix: r.result.matrix,
      }))
      const bytes = await buildWorkbookBytes(tables, {
        title: req.scopeLabel ?? req.projectName ?? 'خروجی',
        generatedAt: new Date().toLocaleString('fa-IR'),
      })
      entries.push({ name: 'data.xlsx', content: bytes })
    } else if (req.format === 'csv') {
      for (const r of dataResults) {
        entries.push({ name: `data/${r.formKey}.csv`, content: buildCsv(r.result.headers, r.result.matrix) })
      }
    } else {
      for (const r of dataResults) {
        const bytes = await buildWorkbookBytes(
          [{ sheetName: r.formKey, headers: r.result.headers, matrix: r.result.matrix }],
          { title: r.title, generatedAt: new Date().toLocaleString('fa-IR') },
        )
        entries.push({ name: `data/${r.formKey}.xlsx`, content: bytes })
      }
    }
  }

  if (schemaJson) entries.push({ name: 'schema.json', content: schemaJson })

  const manifest = {
    bluedatalake_export: 1,
    generated_at: new Date().toISOString(),
    scope: req.scope,
    includes_data: req.includeData,
    includes_schema: req.includeSchema,
    data_format: req.includeData ? (useWorkbook ? 'xlsx-workbook' : req.format) : null,
    schema_source: req.includeSchema ? req.schemaSource : null,
    forms_with_data: dataResults.length,
    forms_skipped_no_data: skipped,
    total_rows: totalRows,
    forms: dataResults.map((r) => ({ form_key: r.formKey, rows: r.result.rowCount })),
  }
  entries.push({ name: 'manifest.json', content: JSON.stringify(manifest, null, 2) })

  const filename = `bluedatalake-${slug}-${req.scope}-${stamp()}.zip`
  await downloadZip(filename, entries)
  return {
    filename,
    formsWithData: dataResults.length,
    formsSkipped: skipped,
    totalRows,
    schemaForms,
    zipped: true,
  }
}
